import client from './client';

export const getChangeOrders = async (status?: number, search?: string) => {
    try {
        const params: Record<string, string | number> = {};
        if (status !== undefined) params.status = status;
        if (search && search.trim()) params.search = search.trim();
        const response = await client.get('/admin/changeOrders', { params });
        return response.data;
    } catch (error) {
        console.error("Error fetching change orders:", error);
        throw error;
    }
};

export const getChangeOrder = async (changeOrderID: number) => {
    try {
        const response = await client.get(`/admin/changeOrders/${changeOrderID}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching change order:", error);
        throw error;
    }
};

export const getEligibleWorkOrders = async (jobNumber: string, keyword?: string) => {
    try {
        const params: Record<string, string> = { jobNumber };
        if (keyword && keyword.trim()) params.keyword = keyword.trim();
        const response = await client.get('/admin/changeOrders/eligibleWorkOrders', { params });
        return response.data;
    } catch (error) {
        console.error("Error fetching eligible work orders:", error);
        throw error;
    }
};

/** TAS-304: read-only preview of the next auto-assigned COR number for a job. */
export const getNextCORNumber = async (jobNumber: string): Promise<{ nextCORNumber: number }> => {
    try {
        const response = await client.get('/admin/changeOrders/nextCORNumber', { params: { jobNumber } });
        return response.data;
    } catch (error) {
        console.error("Error fetching next COR number:", error);
        throw error;
    }
};

/** TAS-304 follow-up: renumber an open change order (duplicate-checked server-side). */
export const updateChangeOrderCORNumber = async (changeOrderID: number, corNumber: number) => {
    try {
        const response = await client.put(`/admin/changeOrders/${changeOrderID}/corNumber`, { corNumber });
        return response.data;
    } catch (error) {
        console.error("Error updating COR number:", error);
        throw error;
    }
};

export const createChangeOrder = async (payload: { jobNumber: string; workOrderIDs: number[]; notes?: string; corNumber?: number }) => {
    try {
        const response = await client.post('/admin/changeOrders', payload);
        return response.data;
    } catch (error) {
        console.error("Error creating change order:", error);
        throw error;
    }
};

export const addWorkOrdersToChangeOrder = async (changeOrderID: number, workOrderIDs: number[]) => {
    try {
        const response = await client.post(`/admin/changeOrders/${changeOrderID}/workOrders`, { workOrderIDs });
        return response.data;
    } catch (error) {
        console.error("Error adding work orders to change order:", error);
        throw error;
    }
};

export const removeWorkOrderFromChangeOrder = async (changeOrderID: number, workOrderID: number) => {
    try {
        const response = await client.delete(`/admin/changeOrders/${changeOrderID}/workOrders/${workOrderID}`);
        return response.data;
    } catch (error) {
        console.error("Error removing work order from change order:", error);
        throw error;
    }
};

export const voidChangeOrder = async (changeOrderID: number, reason: string) => {
    try {
        const response = await client.put(`/admin/changeOrders/${changeOrderID}/void`, { reason });
        return response.data;
    } catch (error) {
        console.error("Error voiding change order:", error);
        throw error;
    }
};

export const noChargeChangeOrder = async (changeOrderID: number, reason: string) => {
    try {
        const response = await client.put(`/admin/changeOrders/${changeOrderID}/noCharge`, { reason });
        return response.data;
    } catch (error) {
        console.error("Error marking change order as No Charge:", error);
        throw error;
    }
};

export const completeChangeOrder = async (changeOrderID: number) => {
    try {
        const response = await client.put(`/admin/changeOrders/${changeOrderID}/complete`);
        return response.data;
    } catch (error) {
        console.error("Error completing change order:", error);
        throw error;
    }
};

export const updateChangeOrderNotes = async (changeOrderID: number, notes: string) => {
    try {
        const response = await client.put(`/admin/changeOrders/${changeOrderID}/notes`, { notes });
        return response.data;
    } catch (error) {
        console.error("Error updating change order notes:", error);
        throw error;
    }
};

export const setWorkOrderCONumber = async (workOrderID: number, coNumber: string) => {
    try {
        const response = await client.put(`/admin/workOrders/${workOrderID}/coNumber`, { coNumber });
        return response.data;
    } catch (error) {
        console.error("Error setting work order CO number:", error);
        throw error;
    }
};

export const setWorkOrderCONumbersBatch = async (changeOrderID: number, workOrderIDs: number[], coNumber: string) => {
    try {
        const response = await client.put(`/admin/changeOrders/${changeOrderID}/coNumbers`, { workOrderIDs, coNumber });
        return response.data;
    } catch (error) {
        console.error("Error setting change order CO numbers:", error);
        throw error;
    }
};

export const clearWorkOrderCONumber = async (workOrderID: number) => {
    try {
        const response = await client.delete(`/admin/workOrders/${workOrderID}/coNumber`);
        return response.data;
    } catch (error) {
        console.error("Error clearing work order CO number:", error);
        throw error;
    }
};

export const openChangeOrderPdf = async (changeOrderID: number) => {
    try {
        const response = await client.get(`/admin/changeOrders/${changeOrderID}/pdf`, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    } catch (error) {
        console.error("Error opening change order PDF:", error);
        throw new Error("Failed to open PDF");
    }
};

export const openChangeOrderPdfVersion = async (changeOrderID: number, versionNumber: number) => {
    try {
        const response = await client.get(`/admin/changeOrders/${changeOrderID}/pdfVersion/${versionNumber}`, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    } catch (error) {
        console.error("Error opening change order PDF version:", error);
        throw new Error("Failed to open PDF version");
    }
};
